import { useTranslation } from "react-i18next";
import { useRovingFocus } from "@/components/ds/useRovingFocus";

export interface SegmentedControlOption<T extends string> {
  id: T;
  label: string;
}

interface SegmentedControlProps<T extends string> {
  value: T | null;
  onChange: (value: T) => void;
  options: ReadonlyArray<SegmentedControlOption<T>>;
  containerClassName?: string;
  buttonClassName?: string;
  activeButtonClassName?: string;
  inactiveButtonClassName?: string;
  ariaLabel?: string;
}

const DEFAULT_CONTAINER_CLASSNAME =
  "inline-flex max-w-full items-center gap-1 overflow-x-auto rounded-full bg-[var(--ds-surface)] p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden";

const DEFAULT_BUTTON_CLASSNAME =
  "min-h-11 shrink-0 rounded-full px-4 py-2 !text-xs !font-medium transition-colors duration-150";
const DEFAULT_ACTIVE_BUTTON_CLASSNAME =
  "bg-[var(--ds-card-sun)] font-medium text-[var(--ds-on-card)]";
const DEFAULT_INACTIVE_BUTTON_CLASSNAME =
  "text-[var(--ds-ink)] hover:bg-[var(--ds-ground)]";

export const SegmentedControl = <T extends string>({
  value,
  onChange,
  options,
  containerClassName = DEFAULT_CONTAINER_CLASSNAME,
  buttonClassName = DEFAULT_BUTTON_CLASSNAME,
  activeButtonClassName = DEFAULT_ACTIVE_BUTTON_CLASSNAME,
  inactiveButtonClassName = DEFAULT_INACTIVE_BUTTON_CLASSNAME,
  ariaLabel,
}: SegmentedControlProps<T>) => {
  const { t } = useTranslation("common");
  const { onKeyDown, setElement } = useRovingFocus(options.map((option) => option.id), onChange);
  const shouldDistributeOptions = options.length >= 3;
  const resolvedContainerClassName = shouldDistributeOptions
    ? `${containerClassName} flex w-full min-w-0 overflow-x-auto`
    : containerClassName;
  const resolvedButtonClassName = shouldDistributeOptions
    ? `${buttonClassName} min-w-max shrink-0 px-3`
    : `${buttonClassName} whitespace-nowrap`;

  return (
    <div role="tablist" aria-label={ariaLabel ?? t("actions.view")} className={resolvedContainerClassName}>
      {options.map((option, index) => (
        <button
          type="button"
          key={option.id}
          onClick={() => onChange(option.id)}
          role="tab"
          aria-selected={value === option.id}
          tabIndex={value === option.id || (value === null && index === 0) ? 0 : -1}
          ref={setElement(index)}
          onKeyDown={(event) => onKeyDown(event, index)}
          className={`${resolvedButtonClassName} ${
            value === option.id ? activeButtonClassName : inactiveButtonClassName
          }`}
        >
          <span className="block whitespace-nowrap leading-tight">
            {option.label}
          </span>
        </button>
      ))}
    </div>
  );
};
